'use client';

import * as React from 'react';
import type {
  ConnectionQuality,
  Participant,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  Room,
} from 'livekit-client';
import { ConnectionState, RoomEvent } from 'livekit-client';
import { collectMeetingTelemetrySnapshot, type TelemetryPrevBundle } from './collectMeetingTelemetrySnapshot';
import type { TelemetrySampleEvent } from './meetingTelemetryTypes';

/** Snapshot interval while connected (same as former NEXT_PUBLIC_MEETING_TELEMETRY_INTERVAL_MS). */
const MEETING_TELEMETRY_INTERVAL_MS = 60_000;
/** Wait before first POST so ICE + connectionQuality often leave `unknown`, and RTC byte deltas exist. */
const MEETING_TELEMETRY_FIRST_SEND_DELAY_MS = 8_000;
const MEETING_TELEMETRY_RECONNECT_SEND_DELAY_MS = 5_000;

const TELEMETRY_DEBUG = process.env.NEXT_PUBLIC_MEETING_TELEMETRY_DEBUG === 'true';

/** Flip to `true` to resume POSTs to `/api/meeting-telemetry`. */
const MEETING_TELEMETRY_SEND_ENABLED = false;

function isTelemetryGloballyEnabled(): boolean {
  return (
    MEETING_TELEMETRY_SEND_ENABLED && process.env.NEXT_PUBLIC_MEETING_TELEMETRY_ENABLED !== 'false'
  );
}

/**
 * Sends periodic snapshots of **this** client's network + track state to
 * `POST /api/meeting-telemetry` (server forwards to evolu-meet-api when configured).
 */
export function useMeetingTelemetry(room: Room | undefined, roomName: string | undefined) {
  const clientInstanceIdRef = React.useRef(
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const prevBundleRef = React.useRef<TelemetryPrevBundle>({
    cameraVideo: null,
    screenVideo: null,
    audio: null,
    remoteByIdentity: {},
  });
  const sampleEventsRef = React.useRef<TelemetrySampleEvent[]>([]);

  React.useEffect(() => {
    if (!room || !roomName || !isTelemetryGloballyEnabled()) {
      return;
    }

    let cancelled = false;
    const intervalMs = MEETING_TELEMETRY_INTERVAL_MS;

    const pushSample = (e: TelemetrySampleEvent) => {
      if (cancelled) return;
      const a = sampleEventsRef.current;
      a.push(e);
      if (a.length > 64) {
        a.splice(0, a.length - 64);
      }
    };

    const onQuality = (quality: ConnectionQuality, participant: Participant) => {
      pushSample({
        type: 'connectionQualityChanged',
        at: new Date().toISOString(),
        connectionQuality: String(quality),
        participantIdentity: participant.identity,
        isLocal: participant.isLocal,
      });
    };

    const onTrackSubscribed = (
      track: RemoteTrack,
      publication: RemoteTrackPublication,
      participant: RemoteParticipant,
    ) => {
      pushSample({
        type: 'trackSubscribed',
        at: new Date().toISOString(),
        trackKind: String(track.kind),
        trackSource: String(publication.source),
        publisherIdentity: participant.identity,
      });
    };

    room.on(RoomEvent.ConnectionQualityChanged, onQuality);
    room.on(RoomEvent.TrackSubscribed, onTrackSubscribed);

    const send = async () => {
      if (cancelled || room.state !== ConnectionState.Connected) return;
      try {
        const payload = await collectMeetingTelemetrySnapshot(
          room,
          roomName,
          clientInstanceIdRef.current,
          prevBundleRef.current,
          () => {
            const out = [...sampleEventsRef.current];
            sampleEventsRef.current = [];
            return out;
          },
        );
        const url = `${window.location.origin}/api/meeting-telemetry`;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        });
        if (TELEMETRY_DEBUG) {
          console.info('[meeting-telemetry] POST', url, res.status);
        }
      } catch (e) {
        if (TELEMETRY_DEBUG) {
          console.warn('[meeting-telemetry] send failed', e);
        }
      }
    };

    const queueSend = () => {
      void send();
    };

    let debounceTimer: number | undefined;
    const scheduleSend = (delayMs: number) => {
      if (debounceTimer !== undefined) {
        window.clearTimeout(debounceTimer);
      }
      debounceTimer = window.setTimeout(() => {
        debounceTimer = undefined;
        queueSend();
      }, delayMs);
    };

    const onConnectionStateChanged = (state: ConnectionState) => {
      if (state === ConnectionState.Connected) {
        scheduleSend(MEETING_TELEMETRY_FIRST_SEND_DELAY_MS);
      }
    };

    room.on(RoomEvent.ConnectionStateChanged, onConnectionStateChanged);
    const id = window.setInterval(queueSend, intervalMs);

    const onReconnect = () => {
      prevBundleRef.current = {
        cameraVideo: null,
        screenVideo: null,
        audio: null,
        remoteByIdentity: {},
      };
      sampleEventsRef.current = [];
      scheduleSend(MEETING_TELEMETRY_RECONNECT_SEND_DELAY_MS);
    };
    room.on(RoomEvent.Reconnected, onReconnect);

    scheduleSend(MEETING_TELEMETRY_FIRST_SEND_DELAY_MS);

    return () => {
      cancelled = true;
      if (debounceTimer !== undefined) {
        window.clearTimeout(debounceTimer);
      }
      window.clearInterval(id);
      room.off(RoomEvent.ConnectionQualityChanged, onQuality);
      room.off(RoomEvent.TrackSubscribed, onTrackSubscribed);
      room.off(RoomEvent.ConnectionStateChanged, onConnectionStateChanged);
      room.off(RoomEvent.Reconnected, onReconnect);
    };
  }, [room, roomName]);
}
